import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://supabase.dibantu.ai/storage/v1/object/public/catalog_indilabs/indilabs_logo_transparent.png";

export const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Fetch the JSON config (cache-busted)
        const res = await fetch(`/config/showcases.json?t=${Date.now()}`);
        const data = await res.json();
        const showcases = data.showcases || [];
        const categories = data.categories || [];

        // 2. Preload all showcase images
        const imageUrls = showcases.map((s) => s.image).filter(Boolean);
        let loaded = 0;
        const total = imageUrls.length || 1;

        const preloadImage = (url) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              loaded++;
              setProgress(Math.round((loaded / total) * 100));
              resolve();
            };
            img.onerror = () => {
              loaded++;
              setProgress(Math.round((loaded / total) * 100));
              resolve();
            };
            img.src = url;
          });

        // Minimum display timer
        const minDisplayTime = new Promise((resolve) => setTimeout(resolve, 2200));

        await Promise.all([...imageUrls.map(preloadImage), minDisplayTime]);

        setProgress(100);
        setTimeout(() => {
          setIsExiting(true);
          setTimeout(() => onComplete(showcases, categories), 500);
        }, 300);
      } catch (err) {
        console.error("Failed to load showcases config:", err);
        setProgress(100);
        setTimeout(() => {
          setIsExiting(true);
          setTimeout(() => onComplete([], []), 500);
        }, 300);
      }
    };

    loadData();
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!isExiting ? (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center"
        >
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-gradient-radial from-primary/6 to-transparent blur-3xl" />

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            className="relative mb-10"
          >
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="bg-muted/50 backdrop-blur-sm rounded-3xl p-5 border border-border/30">
                <img src={LOGO_URL} alt="Indilabs" className="h-16 md:h-20 w-auto object-contain" />
              </div>
            </motion.div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-4 rounded-full border border-primary/15"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-48 md:w-56"
          >
            <div className="h-1 bg-muted rounded-full overflow-hidden mb-3">
              <motion.div
                className="h-full rounded-full"
                style={{ background: "var(--gradient-brand)" }}
                initial={{ width: "0%" }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
            <p className="text-center text-xs text-muted-foreground font-heading">
              Loading showcases... {progress}%
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-[10px] text-muted-foreground/40 tracking-[0.25em] uppercase font-heading"
          >
            innovate · automate · accelerate
          </motion.p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-background"
        />
      )}
    </AnimatePresence>
  );
};
